import { initCSRF, apiGet, apiPost } from './api.js';

// Auth state
let currentUser = null;

/**
 * Check authentication status on page load
 * @returns {Promise<boolean>} True if authenticated, false otherwise
 */
async function checkAuth() {
    try {
        // Initialize CSRF token first (needed for login and other POST requests)
        await initCSRF();
        
        const response = await apiGet('/api/auth/me');
        
        const data = await response.json();
        
        if (data.authenticated && data.user) {
            currentUser = data.user;
            return true;
        } else {
            currentUser = null;
            return false;
        }
    } catch (error) {
        console.error('Auth check failed:', error);
        currentUser = null;
        return false;
    }
}

/**
 * Login with username and password
 * @param {string} username - Username
 * @param {string} password - Password
 * @returns {Promise<Object>} { success: boolean, error?: string }
 */
async function login(username, password) {
    try {
        const response = await apiPost('/api/auth/login', { username, password });
        
        const data = await response.json();
        
        if (response.ok && data.success) {
            currentUser = data.user;
            return { success: true };
        } else {
            return { success: false, error: data.error || 'Login failed' };
        }
    } catch (error) {
        console.error('Login error:', error);
        return { success: false, error: 'Network error' };
    }
}

/**
 * Logout current user
 * @returns {Promise<boolean>} True if successful
 */
async function logout() {
    try {
        await apiPost('/api/auth/logout', {});
        
        currentUser = null;
        return true;
    } catch (error) {
        console.error('Logout error:', error);
        currentUser = null;
        return true; // Clear local state anyway
    }
}

/**
 * Get current user object
 * @returns {Object|null} User object or null if not authenticated
 */
function getCurrentUser() {
    return currentUser;
}

/**
 * Check if user is authenticated
 * @returns {boolean} True if authenticated
 */
function isAuthenticated() {
    return currentUser !== null;
}

/**
 * Check if current user is a repo owner
 * @returns {boolean} True if user has repo_owner role
 */
function isRepoOwner() {
    return currentUser?.isRepoOwner === true;
}

/**
 * Check if current user is an accessibility reviewer
 * @returns {boolean} True if user has accessibility_reviewer role
 */
function isAccessibilityReviewer() {
    return currentUser?.isAccessibilityReviewer === true;
}

/**
 * Check if current user is a god mode user
 * @returns {boolean} True if user has god_mode role
 */
function isGodMode() {
    return currentUser?.isGodMode === true;
}

export { checkAuth, login, logout, getCurrentUser, isAuthenticated, isRepoOwner, isAccessibilityReviewer, isGodMode };
